/**
 * ConditionBuilder - Reusable component for building workflow conditions
 *
 * Provides cascading dropdowns for entity → field → operator → value selection
 * with type-aware value inputs (checkbox for boolean, dropdown for enums, etc.)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Input } from '../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { Checkbox } from '../ui/checkbox.tsx';
import {
  getEntityFields,
  getFieldDefinition,
  getOperatorsForField,
  parseFieldPath,
  buildFieldPath,
  FIELD_TYPES
} from '../../config/workflowEntities';

/**
 * ConditionBuilder component
 *
 * @param {Array<string>} availableEntities - Entities available in this workflow
 * @param {Object} condition - Current condition object {field, operator, value}
 * @param {Function} onChange - Callback when condition changes
 * @param {string} size - Size variant: 'default' | 'compact'
 */
const ConditionBuilder = ({
  availableEntities = [],
  condition = { field: '', operator: '', value: '' },
  onChange,
  size = 'default'
}) => {
  // Parse current field path
  const { entityType: initialEntity, fieldName: initialField } = useMemo(
    () => parseFieldPath(condition.field),
    [condition.field]
  );

  const [selectedEntity, setSelectedEntity] = useState(initialEntity || '');
  const [selectedField, setSelectedField] = useState(initialField || '');
  const [selectedOperator, setSelectedOperator] = useState(condition.operator || '');
  const [value, setValue] = useState(condition.value ?? '');

  // Get field definition for type-aware rendering
  const fieldDefinition = useMemo(() => {
    if (!selectedEntity || !selectedField) return null;
    return getFieldDefinition(selectedEntity, selectedField);
  }, [selectedEntity, selectedField]);

  // Get available fields for selected entity
  const availableFields = useMemo(() => {
    if (!selectedEntity) return [];
    return getEntityFields(selectedEntity);
  }, [selectedEntity]);

  // Get available operators for selected field
  const availableOperators = useMemo(() => {
    if (!selectedEntity || !selectedField) return [];
    return getOperatorsForField(selectedEntity, selectedField);
  }, [selectedEntity, selectedField]);

  // Update condition when any part changes
  useEffect(() => {
    if (selectedEntity && selectedField && selectedOperator) {
      const fieldPath = buildFieldPath(selectedEntity, selectedField);
      onChange({
        field: fieldPath,
        operator: selectedOperator,
        value: value
      });
    }
  }, [selectedEntity, selectedField, selectedOperator, value, onChange]);

  // Handle entity change - reset downstream selections
  const handleEntityChange = (entity) => {
    setSelectedEntity(entity);
    setSelectedField('');
    setSelectedOperator('');
    setValue('');
  };

  // Handle field change - reset operator and value, auto-select first operator
  const handleFieldChange = (field) => {
    setSelectedField(field);
    setValue('');

    // Auto-select first available operator for this field
    const operators = getOperatorsForField(selectedEntity, field);
    if (operators.length > 0) {
      setSelectedOperator(operators[0].value);
    } else {
      setSelectedOperator('');
    }
  };

  // Handle operator change
  const handleOperatorChange = (operator) => {
    setSelectedOperator(operator);
  };

  // Handle value change
  const handleValueChange = (newValue) => {
    setValue(newValue);
  };

  // Size classes
  const inputHeight = size === 'compact' ? 'h-7' : 'h-9';
  const textSize = size === 'compact' ? 'text-xs' : 'text-sm';

  // Render value input based on field type
  const renderValueInput = () => {
    if (!fieldDefinition) {
      return (
        <Input
          placeholder="Select field first..."
          disabled
          className={`${inputHeight} ${textSize} bg-slate-800 border-slate-700 text-slate-400 nodrag`}
        />
      );
    }

    switch (fieldDefinition.type) {
      case FIELD_TYPES.BOOLEAN:
        return (
          <div className="flex items-center space-x-2 h-9 px-3 bg-slate-800 border border-slate-700 rounded-md nodrag">
            <Checkbox
              id={`condition-bool-${selectedField}`}
              checked={value === true || value === 'true'}
              onCheckedChange={(checked) => handleValueChange(checked)}
              className="border-slate-600"
            />
            <label
              htmlFor={`condition-bool-${selectedField}`}
              className={`${textSize} text-slate-200 cursor-pointer`}
            >
              {value === true || value === 'true' ? 'True' : 'False'}
            </label>
          </div>
        );

      case FIELD_TYPES.ENUM:
        if (selectedOperator === 'in') {
          // Multi-select for "in" operator - for now use comma-separated
          return (
            <Input
              placeholder="Enter values (comma-separated)..."
              value={value}
              onChange={(e) => handleValueChange(e.target.value)}
              className={`${inputHeight} ${textSize} bg-slate-800 border-slate-700 text-slate-200 nodrag`}
            />
          );
        }

        // Single select for enum
        return (
          <Select value={value} onValueChange={handleValueChange}>
            <SelectTrigger className={`${inputHeight} ${textSize} bg-slate-800 border-slate-700 text-slate-200 nodrag`}>
              <SelectValue placeholder="Select value..." />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {fieldDefinition.enumValues?.map((enumVal) => (
                <SelectItem
                  key={enumVal.value}
                  value={enumVal.value}
                  className="text-slate-200"
                >
                  {enumVal.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case FIELD_TYPES.NUMBER:
        return (
          <Input
            type="number"
            placeholder="Enter number..."
            value={value}
            onChange={(e) => handleValueChange(e.target.value)}
            className={`${inputHeight} ${textSize} bg-slate-800 border-slate-700 text-slate-200 nodrag`}
          />
        );

      case FIELD_TYPES.DATE:
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleValueChange(e.target.value)}
            className={`${inputHeight} ${textSize} bg-slate-800 border-slate-700 text-slate-200 nodrag`}
          />
        );

      case FIELD_TYPES.DATETIME:
        return (
          <Input
            type="datetime-local"
            value={value}
            onChange={(e) => handleValueChange(e.target.value)}
            className={`${inputHeight} ${textSize} bg-slate-800 border-slate-700 text-slate-200 nodrag`}
          />
        );

      case FIELD_TYPES.EMAIL:
        return (
          <Input
            type="email"
            placeholder="Enter email..."
            value={value}
            onChange={(e) => handleValueChange(e.target.value)}
            className={`${inputHeight} ${textSize} bg-slate-800 border-slate-700 text-slate-200 nodrag`}
          />
        );

      case FIELD_TYPES.PHONE:
        return (
          <Input
            type="tel"
            placeholder="Enter phone..."
            value={value}
            onChange={(e) => handleValueChange(e.target.value)}
            className={`${inputHeight} ${textSize} bg-slate-800 border-slate-700 text-slate-200 nodrag`}
          />
        );

      case FIELD_TYPES.STRING:
      default:
        return (
          <Input
            type="text"
            placeholder="Enter value..."
            value={value}
            onChange={(e) => handleValueChange(e.target.value)}
            className={`${inputHeight} ${textSize} bg-slate-800 border-slate-700 text-slate-200 nodrag`}
          />
        );
    }
  };

  return (
    <div className="space-y-2">
      {/* Entity Selector */}
      <Select value={selectedEntity} onValueChange={handleEntityChange}>
        <SelectTrigger className={`${inputHeight} ${textSize} bg-slate-800 border-slate-700 text-slate-200 nodrag`}>
          <SelectValue placeholder="Select entity..." />
        </SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-700">
          {availableEntities.map((entity) => (
            <SelectItem key={entity} value={entity} className="text-slate-200 capitalize">
              {entity}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Field Selector */}
      <Select
        value={selectedField}
        onValueChange={handleFieldChange}
        disabled={!selectedEntity}
      >
        <SelectTrigger className={`${inputHeight} ${textSize} bg-slate-800 border-slate-700 text-slate-200 nodrag`}>
          <SelectValue placeholder={selectedEntity ? 'Select field...' : 'Select entity first...'} />
        </SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-700 max-h-64">
          {availableFields.map((field) => (
            <SelectItem key={field.field} value={field.field} className="text-slate-200">
              {field.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Operator Selector */}
      <Select
        value={selectedOperator}
        onValueChange={handleOperatorChange}
        disabled={!selectedField}
      >
        <SelectTrigger className={`${inputHeight} ${textSize} bg-slate-800 border-slate-700 text-slate-200 nodrag`}>
          <SelectValue placeholder={selectedField ? 'Select operator...' : 'Select field first...'} />
        </SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-700">
          {availableOperators.map((operator) => (
            <SelectItem key={operator.value} value={operator.value} className="text-slate-200">
              {operator.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value Input (Type-aware) */}
      {renderValueInput()}
    </div>
  );
};

export default ConditionBuilder;
